import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	ClipboardPen,
	Plus,
	RefreshCw,
	Loader as Loader2,
	Printer,
	Mail,
	Play,
	Package,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { useAuth } from '../contexts/AuthContext'
import { getAdminId } from '../utils/getAdminId'
import { useShopStockCounts, type ShopStockCountRecord } from '../hooks/useShopStockCounts'
import { StockCountSessionModal } from '../components/shop-stock-count/StockCountSessionModal'
import {
	buildShopStockCountReportHtml,
	printShopStockCountReport,
} from '../components/shop-stock-count/shop-stock-count-report'
import { fetchCompanyProfile, type CompanyProfile } from '../lib/companyProfile'
import {
	fetchWorkerContacts,
	isEmailConfigured,
	recordShopStockCountDelivery,
	sendShopStockCountReport,
} from '../lib/shopStockCountEmail'

export function ShopStockCount() {
	const { user } = useAuth()
	const adminId = useMemo(() => (user ? getAdminId(user) : null), [user])
	const { counts, openCount, loading, error, load, refresh, clearError } = useShopStockCounts({
		adminId,
		status: null,
		limit: 100,
	})

	const [sessionOpen, setSessionOpen] = useState(false)
	const [continueExisting, setContinueExisting] = useState(false)
	const [company, setCompany] = useState<CompanyProfile | null>(null)
	const [emailBusyId, setEmailBusyId] = useState<string | null>(null)
	const [actionMessage, setActionMessage] = useState<string | null>(null)

	useEffect(() => {
		void load()
	}, [load])

	useEffect(() => {
		if (!adminId) return
		let cancelled = false
		void fetchCompanyProfile(adminId).then((profile) => {
			if (!cancelled) setCompany(profile)
		})
		return () => {
			cancelled = true
		}
	}, [adminId])

	const submitted = useMemo(
		() => counts.filter((c) => c.status === 'submitted'),
		[counts]
	)

	const openNew = () => {
		setContinueExisting(false)
		setSessionOpen(true)
	}

	const openContinue = () => {
		setContinueExisting(true)
		setSessionOpen(true)
	}

	const reprint = useCallback(
		(row: ShopStockCountRecord) => {
			if (!adminId) return
			const html = buildShopStockCountReportHtml({
				countNumber: row.count_number,
				mode: row.mode,
				locationName: row.location_name,
				startedByName: row.started_by_name,
				submittedByName: row.submitted_by_name,
				submittedAt: row.submitted_at || row.started_at,
				lines: row.lines,
				company,
			})
			printShopStockCountReport(html)
			void recordShopStockCountDelivery({
				adminId,
				countId: row.count_id,
				printed: true,
			})
		},
		[adminId, company]
	)

	const resend = useCallback(
		async (row: ShopStockCountRecord) => {
			if (!adminId) return
			setEmailBusyId(row.count_id)
			setActionMessage(null)
			try {
				const configured = await isEmailConfigured(adminId)
				if (!configured) {
					setActionMessage('Email is not set up for this company. Use Print instead.')
					return
				}

				const contacts = await fetchWorkerContacts(adminId)
				const to =
					row.report_email_to ||
					user?.email ||
					contacts[0]?.email ||
					''
				if (!to) {
					setActionMessage('No recipient email available.')
					return
				}

				const html = buildShopStockCountReportHtml({
					countNumber: row.count_number,
					mode: row.mode,
					locationName: row.location_name,
					startedByName: row.started_by_name,
					submittedByName: row.submitted_by_name,
					submittedAt: row.submitted_at || row.started_at,
					lines: row.lines,
					company,
				})

				await sendShopStockCountReport({
					adminId,
					to,
					reportHtml: html,
					summary: {
						countNumber: row.count_number,
						locationName: row.location_name,
						countedByName: row.submitted_by_name,
						lines: row.lines,
					},
				})
				await recordShopStockCountDelivery({ adminId, countId: row.count_id, to })
				setActionMessage(`Report sent to ${to}`)
				void refresh()
			} catch (err) {
				setActionMessage(err instanceof Error ? err.message : 'Email failed')
			} finally {
				setEmailBusyId(null)
			}
		},
		[adminId, company, refresh, user?.email]
	)

	return (
		<div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
						<ClipboardPen className="h-7 w-7 text-teal-700" />
						Shop Stock Count
					</h1>
					<p className="text-sm text-gray-500 mt-1">
						Count shop stock by scanner or one-by-one. Does not change inventory.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="secondary" onClick={() => void refresh()} disabled={loading}>
						<RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
						Refresh
					</Button>
					<Button variant="primary" onClick={openNew} disabled={Boolean(openCount)}>
						<Plus className="h-4 w-4 mr-2" />
						New count
					</Button>
				</div>
			</div>

			{openCount && (
				<div className="rounded-2xl border-2 border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700 p-5 flex flex-wrap items-center justify-between gap-4">
					<div>
						<div className="text-sm font-semibold text-teal-900 dark:text-teal-100">
							Open count in progress
						</div>
						<div className="text-sm text-teal-800/80 dark:text-teal-200/80 mt-1">
							{openCount.count_number} · {openCount.unit_count} units · started{' '}
							{new Date(openCount.started_at).toLocaleString()}
						</div>
					</div>
					<Button variant="primary" onClick={openContinue}>
						<Play className="h-4 w-4 mr-2" /> Continue
					</Button>
				</div>
			)}

			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm flex items-center justify-between gap-3">
					<span>{error.message}</span>
					<button type="button" className="underline" onClick={clearError}>
						Dismiss
					</button>
				</div>
			)}

			{actionMessage && (
				<div className="rounded-lg border border-teal-200 bg-teal-50 text-teal-900 px-4 py-3 text-sm">
					{actionMessage}
				</div>
			)}

			<section>
				<h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
					Previous counts
				</h2>

				{loading && counts.length === 0 ? (
					<div className="flex items-center justify-center py-16 text-gray-500 gap-2">
						<Loader2 className="h-5 w-5 animate-spin" /> Loading…
					</div>
				) : submitted.length === 0 ? (
					<div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 py-16 flex flex-col items-center gap-3 text-gray-500">
						<Package className="h-10 w-10 text-gray-300" />
						<p className="text-sm">No finished stock counts yet</p>
						{!openCount && (
							<Button variant="primary" onClick={openNew}>
								Start the first count
							</Button>
						)}
					</div>
				) : (
					<div className="space-y-2">
						{submitted.map((row) => (
							<div
								key={row.count_id}
								className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
							>
								<div className="min-w-0">
									<div className="font-semibold text-gray-900 dark:text-gray-100">
										{row.count_number}
									</div>
									<div className="text-xs text-gray-500 mt-0.5">
										{row.location_name || 'Shop'} · {row.unit_count} units ·{' '}
										{row.submitted_by_name || '—'} ·{' '}
										{row.submitted_at
											? new Date(row.submitted_at).toLocaleString()
											: '—'}
									</div>
								</div>
								<div className="flex gap-2">
									<Button variant="secondary" size="sm" onClick={() => reprint(row)}>
										<Printer className="h-4 w-4 mr-1" /> Print
									</Button>
									<Button
										variant="secondary"
										size="sm"
										onClick={() => void resend(row)}
										disabled={emailBusyId === row.count_id}
									>
										{emailBusyId === row.count_id ? (
											<Loader2 className="h-4 w-4 animate-spin mr-1" />
										) : (
											<Mail className="h-4 w-4 mr-1" />
										)}
										Email
									</Button>
								</div>
							</div>
						))}
					</div>
				)}
			</section>

			{sessionOpen && (
				<StockCountSessionModal
					adminId={adminId}
					userId={user?.id ?? null}
					userName={user?.full_name ?? null}
					userEmail={user?.email ?? null}
					existing={continueExisting ? openCount : null}
					onClose={() => {
						setSessionOpen(false)
						void refresh()
					}}
					onComplete={() => {
						void refresh()
					}}
				/>
			)}
		</div>
	)
}

export default ShopStockCount
